export interface MetricDefinition {
  id: number; code: string; active: boolean; versionID: number; version: number;
  name: string; description: string; category: string; metricType: string; calculationType: number;
  supportedFrequencies: string; requiredInputs: string; calculationDescription: string; units: string;
  supportsAutomation: boolean; allowsManualEntry: boolean; gamificationEligible: boolean;
  readiness: number; readinessReason: string;
}

export interface KraMetricConfiguration {
  metricDefinitionID: number; metricDefinitionVersionID: number;
  activated?: boolean; manualEntry?: boolean; sourceKey?: string; sourceTimeZoneID?: string; mappingSource?: string; ratingConversion?: MetricRatingConversion;
  targetOperator?: number; targetValue?: number; secondaryTargetValue?: number;
  deadline?: number; deadlineUnit?: string; measurementWindow?: number; rollingDays?: number; oversightRollingDays?: number;
  minimumSample?: number; jobTitleID?: number; employeeID?: number;
  criterionWeight?: number; overallWeight?: number; exceptionRules?: string; sourceConfiguration?: string;
  readiness: number; readinessReason: string; affectsScore: boolean; displayOnly: boolean;
}

export interface KraMetricVersion {
  managerWeight?: number; neutralPolicy?: number; ratingScaleMaximum?: number;
  id: number; version: number; evaluationMethod: number; effectiveStart: string;
  effectiveEnd?: string; approved: boolean; createdOn?: string; metrics: KraMetricConfiguration[];
  criterionWording?: string; kraWording?: string;
}

export interface KraMetricCriterion {
  templateID: number; kraid: number; measurableID: number; kpiid?: number; categoryID?: number;
  kra: string; wording: string; context: string; retired?: boolean; expectedVersionID?: number;
  configuration: KraMetricVersion; history: KraMetricVersion[];
}

export interface MetricRatingConversion {
  type: number; metRating?: number; notMetRating?: number; factor?: number; offset?: number;
  bands: { minimum?: number; maximum?: number; rating: number }[];
}
export interface KraMetricResult {
  id: number; criterionVersionID: number; userID: number; periodStart: string; periodEnd: string; createdOn: string;
  result: { components: { name: string; units?: string; componentType?: string; value?: number; status: number; sample: number; excluded: number;
    periodStart?: string; periodEnd?: string; periodTimeZoneID?: string; targetOperator?: number; targetValue?: number; secondaryTargetValue?: number; pendingReview?: boolean;
    explanation: string; rating?: number; ratingStatus: string; weight?: number; affectsScore: boolean }[];
    managerRating?: number; managerWeight?: number; score?: number; scoreStatus: string; rewardStatus: string; ratingScaleMaximum?: number;
    kra?: string; criterion?: string; configurationVersion?: number; templateID?: number; kraid?: number; measurableID?: number;
    kpiid?: number; categoryID?: number; settingsStatus?: string; asOfUtc?: string; isEmptyCapture?: boolean };
}

// Display labels only. Stored result statuses and historical records keep their original values.
export const metricResultStatuses = ['Goal met', 'Goal not met', 'Not counted', 'Not enough data', 'Setup needed'];
export const metricScoreStatuses: Record<string, string> = {
  'Complete': 'Score ready',
  'Scoring Configuration Incomplete': 'More setup needed to give a score',
  'Incomplete Configuration: neutral weighting policy is not configured': 'Choose how to handle missing or excluded results',
  'Incomplete: unscored component weight preserved': 'Waiting for the missing results',
  'Informational / Not Scored': 'Information only',
  'Excluded from period score': 'Not counted in this period',
  'Manual completion required': 'A result must be entered by hand',
  'Not Scored': 'No score available'
};
export const metricRatingStatuses: Record<string, string> = {
  'Configured': 'Rating ready',
  'Not Configured': 'Set up a rating rule to use this result in the score',
  'Not Configured for this result': 'No rating rule matches this result',
  'Mapping pending review or activation': 'Review and enable this metric before scoring',
  'Configure a conversion within the KRA rating scale': 'The rating must fit the KRA rating scale',
  'Numeric conversion exceeds the supported range': 'The rating is too large. Check the rating rule.'
};
